import { getApiBaseUrl } from "src/config/endpoints";

export interface SupportedDashboardRoute {
  dashboardId: number;
  dashboardName: string;
  route: string;
}

export interface ApiDashboardRouteItem {
  dashboard_id?: number | string | null;
  dashboard_name?: string | null;
}

const getDisplayDashboardName = (
  dashboardName: string | null | undefined,
  fallbackName: string,
) => {
  if (typeof dashboardName === "string") {
    const trimmedName = dashboardName.trim();
    if (trimmedName) {
      return trimmedName;
    }
  }

  return fallbackName;
};

const isDevelopEnvironment = () => {
  try {
    const normalizedUrl = getApiBaseUrl().toLowerCase();
    if (normalizedUrl.includes("demo")) {
      return false;
    }
    if (normalizedUrl.includes("dev")) {
      return true;
    }
  } catch {
    // Ignore missing API base in route config evaluation.
  }

  const hostname = window.location.hostname.toLowerCase();
  if (hostname.includes("demo")) {
    return false;
  }

  return hostname.includes("dev");
};

const isDemoEnvironment = () => {
  try {
    const normalizedUrl = getApiBaseUrl().toLowerCase();
    if (normalizedUrl.includes("demo")) {
      return true;
    }
  } catch {
    // Ignore missing API base in route config evaluation.
  }

  return window.location.hostname.toLowerCase().includes("demo");
};

export const ACCOUNT_APP_BASE = "/account";

export const DASHBOARD_APP_BASE = "/dashboard";

const dashboardAppPath = (slug: string) => `${DASHBOARD_APP_BASE}/${slug}`;

export const DASHBOARD_ROUTE_CONFIG: Record<number, SupportedDashboardRoute> = {
  280: {
    dashboardId: 280,
    dashboardName: "Geo-Spatial Analytics Dashboard",
    route: dashboardAppPath("geo-spatial-analytics-dashboard"),
  },
  293: {
    dashboardId: 293,
    dashboardName: "Web Analytics Dashboard",
    route: dashboardAppPath("web-analytics-dashboard"),
  },
  301: {
    dashboardId: 301,
    dashboardName: "Organic Media Dashboard",
    route: dashboardAppPath("organic-media-dashboard"),
  },
  296: {
    dashboardId: 296,
    dashboardName: "CLTV Dashboard",
    route: dashboardAppPath("cltv-dashboard"),
  },
  302: {
    dashboardId: 302,
    dashboardName: "Paid Media Dashboard",
    route: dashboardAppPath("paid-media-dashboard"),
  },
  303: {
    dashboardId: 303,
    dashboardName: "Customer Attribution Dashboard",
    route: dashboardAppPath("attribution-dashboard"),
  },
  304: {
    dashboardId: 304,
    dashboardName: "Sales & Product Analytics Dashboard",
    route: dashboardAppPath("sales-product-analytics-dashboard"),
  },
  3: {
    dashboardId: 3,
    dashboardName: "Omnichannel Analytics Dashboard",
    route: dashboardAppPath("omnichannel-analytics-dashboard"),
  },
};

/**
 * Raw API `dashboard_id` values that refer to the same logical dashboard as a
 * canonical id above but differ per environment/tenant. Resolved to the
 * canonical id before any lookup. The demo backend sends `305` for Organic
 * Media Dashboard instead of the canonical `301`. The production backend sends
 * `1`-`6`; id `3` (Geo-Spatial) collides with the demo-only Omnichannel id, so
 * it is only aliased outside demo.
 */
const DASHBOARD_ID_ALIASES: Record<number, number> = {
  1: 304,
  2: 293,
  4: 302,
  5: 296,
  6: 301,
  305: 301,
};

const PRODUCTION_GEO_SPATIAL_API_ID = 3;

const resolveCanonicalDashboardId = (dashboardId: number): number => {
  if (dashboardId === PRODUCTION_GEO_SPATIAL_API_ID) {
    return isDemoEnvironment() ? dashboardId : 280;
  }
  return DASHBOARD_ID_ALIASES[dashboardId] ?? dashboardId;
};

/** Map canonical English `dashboardName` (and API fallback titles) to `homepageHeader.dashboardTitles.*` keys. */
export const DASHBOARD_TITLE_I18N_KEY: Record<string, string> = {
  [DASHBOARD_ROUTE_CONFIG[280].dashboardName]: "homepageHeader.dashboardTitles.salesAnalytics",
  [DASHBOARD_ROUTE_CONFIG[293].dashboardName]: "homepageHeader.dashboardTitles.webAnalytics",
  [DASHBOARD_ROUTE_CONFIG[301].dashboardName]: "homepageHeader.dashboardTitles.organicMedia",
  [DASHBOARD_ROUTE_CONFIG[296].dashboardName]: "homepageHeader.dashboardTitles.cltvAnalysis",
  [DASHBOARD_ROUTE_CONFIG[302].dashboardName]: "homepageHeader.dashboardTitles.paidMedia",
  [DASHBOARD_ROUTE_CONFIG[303].dashboardName]: "homepageHeader.dashboardTitles.customerAttribution",
  [DASHBOARD_ROUTE_CONFIG[304].dashboardName]: "homepageHeader.dashboardTitles.salesProductAnalytics",
  [DASHBOARD_ROUTE_CONFIG[3].dashboardName]: "homepageHeader.dashboardTitles.omnichannelAnalytics",
};

export const SUPPORTED_DASHBOARD_IDS = Object.keys(DASHBOARD_ROUTE_CONFIG).map(
  Number,
);

export const SUPPORTED_DASHBOARDS: SupportedDashboardRoute[] = SUPPORTED_DASHBOARD_IDS.map(
  id => DASHBOARD_ROUTE_CONFIG[id],
);

const isDashboardAllowedInCurrentEnvironment = (dashboardId: number) => {
  if (dashboardId === 3) {
    return isDemoEnvironment();
  }

  // if (dashboardId === 304) {
  //   return !isDemoEnvironment() && isDevelopEnvironment();
  // }

  return true;
};

const parseDashboardId = (dashboardId: number | string | null | undefined) => {
  if (typeof dashboardId === "number") {
    return Number.isFinite(dashboardId) ? dashboardId : null;
  }

  if (typeof dashboardId === "string" && dashboardId.trim() !== "") {
    const parsed = Number(dashboardId);
    return Number.isFinite(parsed) ? parsed : null;
  }

  return null;
};

export const getDashboardRouteById = (
  dashboardId: number | string | null | undefined,
): string | null => {
  const normalizedId = parseDashboardId(dashboardId);
  if (normalizedId === null) {
    return null;
  }

  const canonicalId = resolveCanonicalDashboardId(normalizedId);
  if (!isDashboardAllowedInCurrentEnvironment(canonicalId)) {
    return null;
  }

  return DASHBOARD_ROUTE_CONFIG[canonicalId]?.route ?? null;
};

export const getSupportedDashboardById = (
  dashboardId: number | string | null | undefined,
): SupportedDashboardRoute | null => {
  const normalizedId = parseDashboardId(dashboardId);
  if (normalizedId === null) {
    return null;
  }

  const canonicalId = resolveCanonicalDashboardId(normalizedId);
  if (!isDashboardAllowedInCurrentEnvironment(canonicalId)) {
    return null;
  }

  return DASHBOARD_ROUTE_CONFIG[canonicalId] ?? null;
};

export const getSupportedDashboardByRoute = (
  route: string,
): SupportedDashboardRoute | null => {
  return (
    SUPPORTED_DASHBOARDS.find(
      dashboard =>
        dashboard.route === route &&
        isDashboardAllowedInCurrentEnvironment(dashboard.dashboardId),
    ) ?? null
  );
};

/**
 * Match current location to a configured dashboard (exact route or subpath, e.g. meta-ads tab).
 */
export const resolveMatchedDashboardRoute = (
  pathname: string,
): SupportedDashboardRoute | null => {
  const normalized = pathname.replace(/\/+$/, "") || "/";
  const exact = getSupportedDashboardByRoute(normalized);
  if (exact) {
    return exact;
  }
  return (
    SUPPORTED_DASHBOARDS.find(
      d =>
        isDashboardAllowedInCurrentEnvironment(d.dashboardId) &&
        (normalized === d.route || normalized.startsWith(`${d.route}/`)),
    ) ?? null
  );
};

export const getSupportedDashboardByName = (
  dashboardName: string | null | undefined,
): SupportedDashboardRoute | null => {
  if (!dashboardName) {
    return null;
  }

  return (
    SUPPORTED_DASHBOARDS.find(
      dashboard =>
        dashboard.dashboardName === dashboardName &&
        isDashboardAllowedInCurrentEnvironment(dashboard.dashboardId),
    ) ?? null
  );
};

/** Default landing route when backend lists no mappable dashboards (e.g. legacy admin). */
export const DEFAULT_DASHBOARD_FALLBACK = DASHBOARD_ROUTE_CONFIG[280];

/**
 * Dashboards assigned by the backend for this user, in API order, mapped to
 * frontend routes only. No client-side “extra” dashboards — use for access control
 * and post-login navigation. Backend must be the source of truth; this only maps ids/names.
 */
export const buildUserAccessibleDashboardsFromApi = (
  dashboards: ApiDashboardRouteItem[] | null | undefined,
): SupportedDashboardRoute[] => {
  const seenIds = new Set<number>();
  const mappedDashboards: SupportedDashboardRoute[] = [];

  if (Array.isArray(dashboards)) {
    dashboards.forEach(dashboard => {
      const supportedDashboardById = getSupportedDashboardById(
        dashboard?.dashboard_id,
      );
      const supportedDashboard =
        supportedDashboardById ??
        getSupportedDashboardByName(dashboard?.dashboard_name);
      if (!supportedDashboard || seenIds.has(supportedDashboard.dashboardId)) {
        return;
      }

      seenIds.add(supportedDashboard.dashboardId);
      mappedDashboards.push({
        ...supportedDashboard,
        dashboardName: getDisplayDashboardName(
          dashboard?.dashboard_name,
          supportedDashboard.dashboardName,
        ),
      });
    });
  }

  return mappedDashboards;
};

export const getFirstUserAccessibleDashboard = (
  dashboards: ApiDashboardRouteItem[] | null | undefined,
): SupportedDashboardRoute | null => {
  const list = buildUserAccessibleDashboardsFromApi(dashboards);
  return list[0] ?? null;
};
